import {
  extendTheme,
  toThemeVars,
  defaultTheme,
  extendPalette,
} from "@mozioinc/ui/server";
import { toHsla, mix } from "color2k";
import merge from "lodash.merge";
import type {
  WhiteLabelInjectData,
  WhiteLabelDomainData,
  WhiteLabelColorKey,
} from "@mozioinc/query";
import type { Palette, ColorShades } from "@mozioinc/ui/server";

export type TransferWhiteLabelInjectData = WhiteLabelInjectData & {
  config: WhiteLabelInjectData["config"] & {
    header: {
      logo: string;
      showESim: boolean;
      showTransfers: boolean;
      showSignInButton: boolean;
      customLogoLink?: string;
      customLinks?: [string, string][];
      showMyAccount: boolean;
      showMyBookings: boolean;
      headerUsername: "email" | "name";
      headerShowTaSubtext: boolean;
    };
    banners: {
      showTransfers: boolean;
      showESim: boolean;
      showLounges: boolean;
    };
    footer: {
      logo: string;
      phoneNumber: { default: string } & Record<string, string>; // the key is either default or a locale
      emailLink: string;
      socials?: {
        facebook?: string;
        twitter?: string;
        instagram?: string;
        linkedin?: string;
      };
      navLinks?: {
        href: string;
        content: string;
      }[];
      termsAndPrivacyLinks?: {
        href: string;
        content: string;
      }[];
    };
    heroImageUrl?: string;
    showRegisterTA: boolean;
    travelAgentCompany?: string;
    showTAReportButton?: boolean;
    editableMyInformation: boolean;
    emailChangeAbility?: boolean;
    tuiCSAdjustments?: boolean;
    agentIataAndIdEnabled?: boolean;
    passwordResetAbility?: boolean;
    editableTAWithdrawal?: boolean;
    editableBookingFee?: boolean;
    editableCustomerProfiles?: boolean;
    editableCustomerNotifications?: boolean;
    showReservationsFilter?: boolean;
    zendeskEnabled?: boolean;
    helpChatThemeColor?: string;
    helpButtonColor?: string;
    showYourCommission?: boolean;
    noCompactPrice?: boolean;
    requiredCruiseShipName?: boolean;
    showGratuity?: boolean;
    tagManagerKey?: string;
    tagManagerUrlPrefix?: string;
    tagManagerPathnameMap?: [string, string][];
  };
};

export const defaultWhiteLabelData = {
  metadata: {
    title: "Mozio | Your Go-To Ground Transportation Platform",
    description:
      "Mozio is your go-to ground transportation platform. Seamless booking solutions for Travelers, Travel Agents and Businesses. Book with us today!",
    keywords:
      "taxi service, yellow cab, cab, chauffeur, super shuttle, limo, Airport Transfer, Express Train, Ground Transport, ground transportation, Transfer, Transfers, Holiday Taxis, airport transfers, airport taxi, taxis, taxi, transfer, transfers, taxi transfers, hotel transfers, minibus, coach, cities, airport, limousine, shuttles, esim, sim, parking",
    imageUrl: "https://static.mozio.com/assets/logo-og.png",
    faviconUrl: "https://static.mozio.com/assets/favicon.ico",
    canonicalUrl: "https://mozio.com",
    url: "https://mozio.com",
  },
  themeVars: toThemeVars(defaultTheme),
  config: {
    name: "mozio",
    partnerRef: "mozio_web",
    domain: "mozio.com",
    locale: "en-us",
    currency: "USD",
    authRequired: false,
    showResendBooking: true,
    header: {
      logo: "https://static.mozio.com/assets/logo.svg",
      showESim: true,
      showTransfers: true,
      showSignInButton: true,
      showMyAccount: true,
      showMyBookings: true,
      headerUsername:
        "email" as TransferWhiteLabelInjectData["config"]["header"]["headerUsername"],
      headerShowTaSubtext: true,
    },
    banners: {
      showTransfers: true,
      showESim: true,
      showLounges: true,
    },
    footer: {
      logo: "https://static.mozio.com/assets/logo.svg",
      phoneNumber: {
        default: "+1 (855) 980-7116",
      },
      emailLink: "travel-extras@mozio.com",
    },
    showRegisterTA: true,
    travelAgentCompany: "",
    showTAReportButton: true,
    editableMyInformation: true,
    emailChangeAbility: true,
    tuiCSAdjustments: false,
    passwordResetAbility: true,
    agentIataAndIdEnabled: false,
    editableTAWithdrawal: true,
    editableBookingFee: true,
    includePlatformFee: true,
    editableCustomerProfiles: true,
    editableCustomerNotifications: true,
    showReservationsFilter: false,
    zendeskEnabled: true,
    showYourCommission: true,
    noCompactPrice: false,
    requiredCruiseShipName: true,
    showGratuity: true,
    tagManagerKey: "GTM-TZGXRC",
  },
} satisfies TransferWhiteLabelInjectData;

export const mapWhiteLabelInjectData = (
  data: WhiteLabelDomainData,
): TransferWhiteLabelInjectData => {
  const { domain, locale, currency, config } = data;

  const palette = extendPalette({
    primary: toThemeColors(
      config.FIRST_BACKGROUND_COLOR,
      config.FIRST_CONTRAST_COLOR,
    ),
    secondary: toThemeColors(
      config.FOURTH_BACKGROUND_COLOR,
      config.FOURTH_CONTRAST_COLOR,
    ),
  });

  const whiteLabelTheme = extendTheme({
    palette,
    header: {
      logoHeight: config.HEADER_CUSTOM_LOGO_HEIGHT || undefined,
      height: config.HEADER_CUSTOM_HEIGHT || undefined,
      bg: getColor(palette, config.HEADER_BG),
      border: getColor(palette, config.HEADER_BORDER_COLOR),
      createAccountBtn: {
        bg: getColor(palette, config.HEADER_CREATE_ACCOUNT_BTN_BG),
        text: getColor(palette, config.HEADER_CREATE_ACCOUNT_BTN_TEXT),
        hover: {
          bg: getColor(palette, config.HEADER_CREATE_ACCOUNT_BTN_HOVER_BG),
          text: getColor(palette, config.HEADER_CREATE_ACCOUNT_BTN_HOVER_TEXT),
        },
      },
      signInBtn: {
        bg: getColor(palette, config.HEADER_SIGN_IN_BTN_BG),
        text: getColor(palette, config.HEADER_SIGN_IN_BTN_TEXT),
        hover: {
          bg: getColor(palette, config.HEADER_SIGN_IN_BTN_HOVER_BG),
          text: getColor(palette, config.HEADER_SIGN_IN_BTN_HOVER_TEXT),
        },
      },
      generalBtn: {
        bg: getColor(palette, config.HEADER_GENERAL_BTN_BG),
        text: getColor(palette, config.HEADER_GENERAL_BTN_TEXT),
        hover: {
          bg: getColor(palette, config.HEADER_GENERAL_BTN_HOVER_BG),
          text: getColor(palette, config.HEADER_GENERAL_BTN_HOVER_TEXT),
        },
      },
    },
  });

  return merge({}, defaultWhiteLabelData, {
    metadata: {
      imageUrl:
        config.CUSTOM_SOCIAL_LOGO ||
        config.CUSTOM_LOGO ||
        "https://static.mozio.com/assets/logo-og.png",
      faviconUrl: config.CUSTOM_FAVICON,
    },
    themeVars: toThemeVars(whiteLabelTheme),
    config: {
      id: config.ID,
      name: config.NAME,
      domain,
      locale,
      currency: currency || config.DEFAULT_CURRENCY || undefined,
      partnerRef: config.PARTNER_REF,
      heroImageUrl: config.PARKING_COVER_IMAGE,
      showResendBooking: config.SHOW_RESEND_BOOKING,
      showRegisterTA: config.REGISTER_TA_SHOW,
      showTAReportButton: config.SHOW_TA_REPORT_BUTTON,
      travelAgentCompany: config.TRAVEL_AGENT_COMPANY,
      autoDetectCurrency: config.AUTODETECT_CURRENCY_BY_IP,
      editableMyInformation: config.EDITABLE_MY_INFORMATION,
      emailChangeAbility: config.EMAIL_CHANGE_ABILITY,
      tuiCSAdjustments: config.TUI_CS_ADJUSTMENTS,
      passwordResetAbility: config.PASSWORD_RESET_ABILITY,
      agentIataAndIdEnabled: config.AGENT_IATA_AND_ID_ENABLED,
      editableTAWithdrawal: config.EDITABLE_TA_WITHDRAWAL,
      editableBookingFee: config.EDITABLE_BOOKING_FEE,
      includePlatformFee: config.INCLUDE_PLATFORM_FEE,
      header: {
        logo: config.CUSTOM_LOGO || undefined,
        showESim: config.PARKING_SHOW_HEADER_ESIM,
        showTransfers: config.PARKING_SHOW_HEADER_TRANSFER,
        showSignInButton: config.SHOW_SIGNIN_BUTTON,
        customLogoLink: config.CUSTOM_LOGO_LINK || undefined,
        customLinks: config.HEADER_CUSTOM_LINKS,
        showMyAccount: config.SHOW_MY_ACCOUNT,
        showMyBookings: config.SHOW_MY_BOOKINGS,
        headerUsername: config.HEADER_USER_NAME,
        headerShowTaSubtext: config.HEADER_SHOW_TA_SUBTEXT,
      },
      banners: {
        showTransfers: config.PARKING_SHOW_TRANSFER_BANNER,
        showESim: config.PARKING_SHOW_ESIM_BANNER,
        showLounges: config.PARKING_SHOW_LOUNGES_BANNER,
      },
      footer: {
        logo: config.CUSTOM_FOOTER_LOGO || undefined,
        socials: Object.fromEntries(config.FOOTER_SOCIALS),
        phoneNumber: config.ESIM_SUPPORT_PHONE_NUMBERS
          ? Object.fromEntries(config.ESIM_SUPPORT_PHONE_NUMBERS)
          : undefined,
        emailLink: config.ESIM_SUPPORT_EMAIL,
      },
      zendeskEnabled: config.ZENDESK_ENABLED,
      helpChatThemeColor: config[config.HELP_CHAT_THEME_COLOR],
      helpButtonColor: config[config.HELP_BUTTON_COLOR],
      showYourCommission: config.SHOW_YOU_EARNED,
      noCompactPrice: config.NO_COMPACT_PRICE,
      requiredCruiseShipName: config.REQUIRED_CRUISE_SHIP_NAME,
      showGratuity: config.GRATUITY_SHOW ?? false,
      tagManagerKey: config.TAG_MANAGER_KEY,
      tagManagerUrlPrefix: config.TAG_MANAGER_URL_PREFIX,
      tagManagerPathnameMap: config.TAG_MANAGER_PATHNAME_MAP,
    },
  });
};

const toHsl = (hsla: string) => {
  return hsla.replace("hsla", "hsl").replace(", 1)", ")").replaceAll(",", "");
};

const isValidHexColor = (color: string) =>
  /^#([\dA-Fa-f]{3}){1,2}$/.test(color);

const toThemeColors = (color: string, contrastColor: string) => {
  if (!isValidHexColor(color)) {
    return undefined;
  }

  return {
    100: toHsl(toHsla(mix(color, "#FFFFFF", 0.8))),
    200: toHsl(toHsla(mix(color, "#FFFFFF", 0.3))),
    300: toHsl(toHsla(color)),
    400: toHsl(toHsla(mix(color, "#000000", 0.3))),
    500: toHsl(toHsla(mix(color, "#000000", 0.6))),
    contrast: toHsl(toHsla(contrastColor)),
  };
};

const getColor = (palette: Palette, colorKey: WhiteLabelColorKey): string => {
  const [color, shade] = colorKey.split("-") as [
    keyof Palette,
    keyof ColorShades,
  ];
  const colorValue = palette[color];
  if (typeof colorValue === "string") {
    return colorValue;
  }
  return palette[color][shade];
};
